export const snakeCase = (str: string): string =>
  str
    .split(/(?=[A-Z])/)
    .join("_")
    .toLowerCase();

export function randomString(length: number): string {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);
  let result = "";
  for (const v of randomValues) {
    result += characters.charAt(v % charactersLength);
  }
  return result;
}

/**
 * Finds email addresses inside free-form text (see `isEmail` for whole-string
 * validation).
 *
 * The whole pattern is wrapped in one capture group on purpose, so
 * `String.prototype.split(EMAIL_REGEX)` keeps the matched emails in its result
 * array (they land at the odd indices). It is intentionally non-global, so a
 * shared instance stays safe to reuse with `.test()` (no `lastIndex` state).
 */
export const EMAIL_REGEX = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;

// Anchored form of EMAIL_REGEX, for validating that an entire string is an email.
const EMAIL_EXACT_REGEX = new RegExp(`^${EMAIL_REGEX.source}$`);

/** Returns true when `value` is a well-formed email address (whole-string match). */
export const isEmail = (value: string): boolean => EMAIL_EXACT_REGEX.test(value);

/**
 * Sanitizes a string for safe use in a data-testid (or DOM id): lowercases it,
 * replaces any character outside `[a-z0-9-_]` with a hyphen, then collapses
 * consecutive hyphens and trims leading/trailing ones.
 *
 * `value` is typed as `string`, but callers sometimes spread untyped/API data
 * where it can arrive as a non-string at runtime, so coerce defensively here
 * rather than relying on every call site to remember to do it.
 */
export const sanitizeTestId = (value: string): string => {
  const stringValue = typeof value === "string" ? value : String(value);
  return stringValue
    .toLowerCase()
    .replace(/[^a-z0-9-_]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
};

export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";

  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }

  return btoa(binary);
}
